import { ReactNode } from 'react'
import clsx from 'clsx'
import { Button } from './Button'

interface EmptyStateProps {
  icon?: React.ReactNode
  title: string
  description: string
  action?: {
    label: string
    onClick: () => void
  }
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  className?: string
  children?: ReactNode
}

/**
 * Brand-styled empty state component
 *
 * Following brand guidelines:
 * - Positive framing (empty = good, not broken)
 * - Helpful illustration (not sad robot)
 * - Clear explanation of what would appear
 * - Action to take instead
 *
 * Example good copy:
 * - "No escalations right now - That's a good thing!"
 * - "All caught up - No pending claims to review"
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  children,
}: EmptyStateProps) {
  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center py-16 px-6 text-center',
        className
      )}
    >
      {icon ? (
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-card bg-surface-alt text-ink-faint">{icon}</div>
      ) : (
        <DefaultEmptyIcon />
      )}

      <h3 className="font-outfit text-h4 text-ink mb-2">{title}</h3>
      <p className="font-inter text-body-sm text-ink-muted max-w-md mb-6">
        {description}
      </p>

      {(action || secondaryAction) && (
        <div className="flex items-center gap-3">
          {action && (
            <Button variant="primary" onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}

      {children}
    </div>
  )
}

/**
 * Default illustration - abstract growth/calm scene
 * Following brand guidelines: geometric, soft, encouraging
 */
function DefaultEmptyIcon() {
  return (
    <svg
      width="120"
      height="120"
      viewBox="0 0 120 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="mb-4"
    >
      {/* Background circle */}
      <circle cx="60" cy="60" r="56" fill="#EDF6F9" />

      {/* Abstract plant/growth element */}
      <path
        d="M60 85V55"
        stroke="#83C5BE"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M60 55C60 55 50 50 50 40C50 30 60 35 60 35"
        stroke="#83C5BE"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M60 55C60 55 70 50 70 40C70 30 60 35 60 35"
        stroke="#006D77"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M60 65C60 65 45 62 42 52"
        stroke="#83C5BE"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M60 65C60 65 75 62 78 52"
        stroke="#006D77"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />

      {/* Ground/base */}
      <ellipse cx="60" cy="88" rx="20" ry="4" fill="#E29578" opacity="0.3" />

      {/* Small decorative dots */}
      <circle cx="35" cy="45" r="3" fill="#E29578" opacity="0.5" />
      <circle cx="85" cy="50" r="2" fill="#83C5BE" opacity="0.5" />
      <circle cx="40" cy="70" r="2" fill="#006D77" opacity="0.3" />
      <circle cx="80" cy="35" r="2.5" fill="#E29578" opacity="0.4" />
    </svg>
  )
}

/**
 * Preset empty states for common scenarios
 */
export const EmptyStatePresets = {
  noEscalations: {
    title: 'No escalations right now',
    description:
      "That's a good thing! Amani's handling conversations smoothly. Check back later or review recent chats.",
  },
  noConversations: {
    title: 'Conversations will appear here',
    description:
      'When clients start chatting with Amani, their conversations will show up in this list.',
  },
  noResults: {
    title: 'No results found',
    description:
      "We couldn't find anything matching your search. Try adjusting your filters or search terms.",
  },
  noFamilies: {
    title: 'No groups yet',
    description:
      'Upload your balance sheet to get started. Groups will appear here once imported.',
  },
}
